<script setup lang="ts">
import { useAdminStore } from '@voix/store/adminStore'
import type { SliceConfigInterface } from '@voix/types'
import { computed, ref } from 'vue'
import type { Ref } from 'vue'
import { useSliceStore } from '@voix/store/sliceStore'
import TextInput from '../chrome/controls/TextInput.vue'
import { useCookie } from '#imports'

const adminStore = useAdminStore()
const sliceStore = useSliceStore()

const nameFilter: Ref<string> = ref('')
const sliceList: Record<string, SliceConfigInterface> = sliceStore.slices

const sliceGroups = computed(() => {
  const groups: Array<{ [key: string]: Array<SliceConfigInterface> }> = []

  Object.keys(sliceList).forEach((sliceName) => {
    if (!sliceName.toLowerCase().includes(nameFilter.value.toLowerCase()))
      return

    const slice = sliceList[sliceName]
    const group = slice.name.group || 'Global'

    // If the group doesn't exist yet, create it
    const groupIndex = groups.findIndex(g => g[group])
    if (groupIndex === -1) {
      groups.push({
        [group]: [slice],
      })
    }
    else {
      groups[groupIndex][group].push(slice)
    }
  })

  // sort the groups by the group key
  groups.sort((a, b) => {
    if (Object.keys(a)[0] < Object.keys(b)[0])
      return -1
    if (Object.keys(a)[0] > Object.keys(b)[0])
      return 1
    return 0
  })

  // sort the group slices by name
  groups.forEach((group) => {
    Object.keys(group).forEach((groupName) => {
      group[groupName].sort((a, b) => {
        if (a.name.label < b.name.label)
          return -1
        if (a.name.label > b.name.label)
          return 1
        return 0
      })
    })
  })

  return groups
})

const openGroups: Ref<Array<string>> = ref([])

function openGroup(group: string) {
  if (openGroups.value.includes(group))
    openGroups.value = openGroups.value.filter(g => g !== group)

  else
    openGroups.value.push(group)
}

const slicePreviewSliceCookie = useCookie('voix-slice-preview-slice')

function selectSlice(sliceName: SliceConfigInterface['name']) {
  adminStore.currentlySelectedTemplatizerSlice = sliceName
  slicePreviewSliceCookie.value = sliceName as unknown as string
}

if (slicePreviewSliceCookie.value)
  selectSlice(slicePreviewSliceCookie.value as unknown as SliceConfigInterface['name'])
</script>

<template>
  <div>
    <div class="px-4">
      <TextInput id="slice-name-filter" v-model="nameFilter" placeholder="Filter Slices" />
    </div>

    <ul class="mt-4 flex flex-col text-xs text-gray-600 space-y-2 px-2">
      <li v-for="(group, key) in sliceGroups" :key="key">
        <button class="w-full flex items-center justify-start space-x-1 font-medium voix-admin-text-dark text-sm hover:voix-admin-bg-lightest px-1 py-1 rounded" @click="openGroup(Object.keys(group)[0])">
          <Icon class="w-4 h-4 voix-admin-text-light" :class="{ 'rotate-90': openGroups.includes(Object.keys(group)[0]) }" name="heroicons:chevron-right-20-solid" />
          <span class="uppercase text-xs font-bold">{{ Object.keys(group)[0] }}</span>
        </button>
        <ul class="overflow-hidden flex flex-col space-y-1" :class="{ 'h-0': !openGroups.includes(Object.keys(group)[0]), 'pb-4 ': openGroups.includes(Object.keys(group)[0]) }">
          <li v-for="slice in group[Object.keys(group)[0]]" :key="`${slice.name.label}${slice.name.group}`">
            <button
              class="group w-full flex items-center transition-colors duration-200 text-sm hover:voix-admin-bg-lightest px-3 pl-7 py-1 rounded"
              :class="{
                'font-bold voix-admin-text-dark': adminStore.currentlySelectedTemplatizerSlice.label === slice.name.label,
                'text-gray-500': adminStore.currentlySelectedTemplatizerSlice.label !== slice.name.label,
              }"
              @click="selectSlice(slice.name)"
            >
              <span class="text-xs voix-admin-text text-left">{{ slice.name.label }}</span>
            </button>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>
